/**
 * Voices of the course on the web (D-107): a text read in any language with the browser's
 * voices (speechSynthesis), a dialogue read with one voice per speaker (the labels are never read),
 * and « Listen to the explanation » (server audio, else the device, else French or English).
 * The choices themselves live in @agencyos/core, shared with the phone.
 */

import { type ListenPlan, deviceSpeech, pickVoice, planVoices, splitScript } from "@agencyos/core";

import { type UtterResult, claimPlayback, deviceVoices, speechAvailable, stopSpeaking, utter } from "../audio";

function say(text: string, locale: string, voice: SpeechSynthesisVoice | undefined, rate = 0.95, pitch = 1): Promise<UtterResult> {
  return utter(text, { lang: locale, voice, rate, pitch }); // never hangs (D-114)
}

/** Reads a text in a locale (fr-FR for the TCF content); resolves when finished. */
export async function speakIn(text: string, locale: string, rate = 0.95): Promise<void> {
  if (!speechAvailable() || !text.trim()) return;
  const list = await deviceVoices();
  stopSpeaking();
  await say(text, locale, pickVoice(list, locale), rate);
}

/** A listening script « ROLE (F) : line »: one voice per character (the CO module's split and
 * voice plan, @agencyos/core `listening`), the labels and markers never read. */
export async function speakDialogue(script: string, locale: string): Promise<void> {
  if (!speechAvailable() || !script.trim()) return;
  const list = await deviceVoices();
  const lines = splitScript(script);
  const devices = list.map((v, i) => ({ id: v.voiceURI || `${v.name}-${i}`, name: v.name, lang: v.lang }));
  const plan = planVoices(lines, devices, (locale.split("-")[0] ?? "fr").toLowerCase(), locale);
  stopSpeaking();
  for (const line of lines) {
    const choice = plan.get(line.index);
    const index = devices.findIndex((d) => d.id === choice?.voiceId);
    const outcome = await say(line.text, locale, index >= 0 ? list[index] : pickVoice(list, locale), choice?.rate ?? 0.95, choice?.pitch ?? 1);
    if (outcome === "stopped" || outcome === "blocked") return; // « Stop », or no gesture yet
  }
}

/** Can this device read a locale? (a voice of the language is installed) */
export async function hasVoice(locale: string): Promise<boolean> {
  if (!speechAvailable()) return false;
  return pickVoice(await deviceVoices(), locale) !== undefined;
}

/** How « Listen to the explanation » was read: the server's voice, the device's voice of the
 * language, French / English instead, or nothing (the browser cannot speak). */
export type ExplanationVoice = "server" | "device" | "fallback" | "none";

/** The server's audio to its end (or its failure), exclusive of any other player. */
function playToEnd(audio: HTMLAudioElement, url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      audio.removeEventListener("ended", ended);
      audio.removeEventListener("error", failed);
      audio.removeEventListener("pause", paused);
      resolve(ok);
    };
    const ended = () => done(true);
    const failed = () => done(false);
    const paused = () => done(true); // « Stop »
    audio.addEventListener("ended", ended);
    audio.addEventListener("error", failed);
    audio.src = url;
    claimPlayback(audio);
    audio.play().then(
      () => audio.addEventListener("pause", paused),
      () => done(false),
    );
  });
}

/**
 * Plays « Listen to the explanation » and resolves when it is over: the server's audio when
 * ready, else (or when it cannot be played) the device's voice of the language, else French /
 * English (D-107, D-114).
 */
export async function playExplanation(plan: ListenPlan, audio: HTMLAudioElement | null): Promise<ExplanationVoice> {
  if (plan.mode === "server" && plan.status === "ready" && plan.audioUrl && audio) {
    if (await playToEnd(audio, plan.audioUrl)) return "server";
    // unplayable (network, expired link): the device reads it instead
  }
  if (!speechAvailable()) return "none";
  const spoken = deviceSpeech(plan, await deviceVoices());
  stopSpeaking();
  await say(spoken.text, spoken.locale, spoken.voice);
  return spoken.fallback ? "fallback" : "device";
}
